import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { checkAndAwardTrophies } from '../utils/trophyManager';
import type { ForumComment } from '../types/forum';
import { useAuth } from './useAuth';

export function useForumComment() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  const submitComment = async (
    topicId: string,
    content: string,
    parentId?: string
  ) => {
    if (!user) {
      throw new Error('Must be logged in to comment');
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get user profile first
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('display_name')
        .eq('id', user.id)
        .single();

      if (profileError) {
        if (profileError.message.includes('Failed to fetch')) {
          throw new Error('Unable to connect to the server. Please check your internet connection.');
        }
        throw profileError;
      }

      // Submit the comment
      const { data: comment, error: submitError } = await supabase
        .from('forum_comments')
        .insert([{
          topic_id: topicId,
          user_id: user.id,
          content,
          parent_id: parentId
        }])
        .select(`
          *,
          user:user_profiles!user_id(display_name)
        `)
        .single();

      if (submitError) {
        if (submitError.message.includes('Failed to fetch')) {
          throw new Error('Unable to connect to the server. Please check your internet connection.');
        }
        throw submitError;
      }

      // Check for trophies only if comment was successfully created
      if (comment) {
        try {
          await checkAndAwardTrophies(user.id);
        } catch (trophyError) {
          console.error('Trophy check failed:', trophyError);
          // Don't throw here - trophy errors shouldn't prevent comment submission
        }
      }

      return {
        ...comment,
        user: {
          display_name: profile?.display_name || 'Anonymous User'
        }
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to submit comment';
      setError(new Error(message));
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const likeComment = async (commentId: string) => {
    if (!user) {
      throw new Error('Must be logged in to like comments');
    }

    try {
      setIsLoading(true);
      setError(null);

      // Check if user has already liked this comment
      const { data: likes, error: likesError } = await supabase
        .from('comment_likes')
        .select('id')
        .eq('comment_id', commentId)
        .eq('user_id', user.id);

      if (likesError) throw likesError;

      const hasLiked = likes && likes.length > 0;

      if (hasLiked) {
        // Unlike if already liked
        const { error: unlikeError } = await supabase
          .from('comment_likes')
          .delete()
          .eq('comment_id', commentId)
          .eq('user_id', user.id);

        if (unlikeError) throw unlikeError;

        // Decrement likes count
        const { error: decrementError } = await supabase
          .rpc('decrement_comment_likes', {
            comment_id: commentId
          });

        if (decrementError) throw decrementError;
      } else {
        // Add new like
        const { error: likeError } = await supabase
          .from('comment_likes')
          .insert([{
            comment_id: commentId,
            user_id: user.id
          }]);

        if (likeError) throw likeError;

        // Increment likes count
        const { error: incrementError } = await supabase
          .rpc('increment_comment_likes', {
            comment_id: commentId
          });

        if (incrementError) throw incrementError;
      }
    } catch (err) {
      console.error('Error toggling like:', err);
      setError(err instanceof Error ? err : new Error('Failed to toggle like'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { submitComment, likeComment, isLoading, error };
}